import { Alert, Button, Checkbox, Popover, Select } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  OpenClass,
  OpenClassRegistrationSelectionParams,
  OpenClassStatus,
} from '../../openClasses/types/OpenClass';
import {
  formatSessionTimeRange,
  openClassOptions,
  certificateTemplateOptions,
} from '../../openClasses/utils/openClass';
import {
  getDefaultAssignableSessionIds,
  remainingSeats,
  sessionSeatsSelection,
} from '../utils/openClassAssignment';
import { OpenClassAssignmentFormValues } from './OpenClassAssignmentFields';

type OpenClassBulkAssignmentBarProps = {
  className?: string;
  openClasses: OpenClass[];
  selectedCount: number;
  onApply: (selection: OpenClassRegistrationSelectionParams) => void;
};

const OpenClassBulkAssignmentBar: React.FC<OpenClassBulkAssignmentBarProps> = ({
  className,
  openClasses,
  selectedCount,
  onApply,
}) => {
  const { t } = useTranslation();
  const [assignment, setAssignment] = useState<OpenClassAssignmentFormValues>(
    {},
  );
  const selectedOpenClass = openClasses.find(
    ({ id }) => id === assignment.openClassId,
  );
  const selectedSessionIds = useMemo(
    () => assignment.sessionIds ?? [],
    [assignment.sessionIds],
  );
  const hasSelectedParticipants = selectedCount > 0;

  const classOptions = useMemo(
    () => openClassOptions(openClasses),
    [openClasses],
  );

  const certificateOptions = certificateTemplateOptions(
    selectedOpenClass?.certificateTemplates,
  );

  const sessionOptions = useMemo(
    () =>
      // Server seat counts, so unsaved picks aren't subtracted. The backend rejects
      // what no longer fits.
      (selectedOpenClass?.sessions ?? []).map((session) => {
        const remaining = remainingSeats(session);
        const time = formatSessionTimeRange(session);
        const seatsSelection = sessionSeatsSelection(session, selectedCount);
        return {
          // A full session stays clickable while picked, so it can still be unpicked.
          disabled:
            session.status === OpenClassStatus.Canceled ||
            (!seatsSelection && !selectedSessionIds.includes(session.id)),
          location: session.location,
          remaining,
          seatsSelection,
          time,
          value: session.id,
        };
      }),
    [selectedCount, selectedOpenClass, selectedSessionIds],
  );

  const shortSessions = useMemo(
    () =>
      sessionOptions
        .filter(
          (session) =>
            selectedSessionIds.includes(session.value) &&
            !session.seatsSelection,
        )
        .map((session) => `${session.time} (${session.remaining})`),
    [selectedSessionIds, sessionOptions],
  );

  const onOpenClassChange = useCallback(
    (openClassId: number) => {
      const openClass = openClasses.find(({ id }) => id === openClassId);
      setAssignment({
        certificateTemplateId: undefined,
        openClassId,
        sessionIds: getDefaultAssignableSessionIds(openClass),
      });
    },
    [openClasses],
  );

  const canAssign =
    hasSelectedParticipants &&
    !!assignment.certificateTemplateId &&
    !!assignment.openClassId &&
    selectedSessionIds.length > 0 &&
    shortSessions.length === 0;

  const submit = useCallback(() => {
    if (!canAssign) return;

    onApply({
      certificateTemplateId: assignment.certificateTemplateId!,
      openClassId: assignment.openClassId!,
      sessionIds: assignment.sessionIds!,
    });
    setAssignment({});
  }, [assignment, canAssign, onApply]);

  return (
    <div className={className}>
      <div className="rounded-lg border border-gray-200 bg-white p-3 text-gray-700 shadow-sm">
        {hasSelectedParticipants && shortSessions.length > 0 && (
          <Alert
            className="mb-3"
            message={t('views.OpenClassBookingOrderDetails.sessionsTooSmall', {
              count: selectedCount,
              sessions: shortSessions.join(', '),
            })}
            showIcon
            type="warning"
          />
        )}
        <div className="flex flex-wrap items-center gap-2">
          <span className="whitespace-nowrap">
            {t('views.OpenClassBookingOrderDetails.attendeesSelected', {
              count: selectedCount,
            })}
          </span>
          <Select
            className="w-80"
            disabled={!hasSelectedParticipants}
            options={classOptions}
            placeholder={t('views.OpenClassBookingOrderDetails.mapToOpenClass')}
            popupMatchSelectWidth={false}
            showSearch
            value={assignment.openClassId}
            onChange={onOpenClassChange}
          />
          <Select
            className="min-w-32"
            disabled={!hasSelectedParticipants || !selectedOpenClass}
            options={certificateOptions}
            placeholder={t('views.OpenClasses.certificateType')}
            value={assignment.certificateTemplateId}
            onChange={(certificateTemplateId) =>
              setAssignment((current) => ({
                ...current,
                certificateTemplateId,
              }))
            }
          />
          <Popover
            content={
              <div className="flex max-w-3xl flex-col gap-2">
                {sessionOptions.map((session) => {
                  const selected = selectedSessionIds.includes(session.value);
                  const availabilityClassName = !session.seatsSelection
                    ? 'bg-red-50 text-red-700'
                    : session.remaining <= 2
                      ? 'bg-orange-50 text-orange-700'
                      : 'bg-green-50 text-green-700';

                  return (
                    <Checkbox
                      checked={selected}
                      className="m-0 rounded-md border border-gray-200 px-3 py-2 hover:border-safeLifeMedium"
                      disabled={session.disabled}
                      key={session.value}
                      onChange={(event) =>
                        setAssignment((current) => {
                          const currentIds = current.sessionIds ?? [];
                          return {
                            ...current,
                            sessionIds: event.target.checked
                              ? [...currentIds, session.value]
                              : currentIds.filter((id) => id !== session.value),
                          };
                        })
                      }>
                      <span className="ml-2 flex min-w-0 items-center gap-4">
                        <span className="min-w-0 grow">
                          <span className="block font-medium text-gray-700">
                            {session.time}
                          </span>
                          {session.location && (
                            <span className="block truncate text-sm text-gray-500">
                              {session.location}
                            </span>
                          )}
                        </span>
                        <span
                          className={`shrink-0 rounded-full px-2 py-1 text-sm font-medium ${availabilityClassName}`}>
                          {t('views.OpenClassBookingOrderDetails.seatsLeft', {
                            count: session.remaining,
                          })}
                        </span>
                      </span>
                    </Checkbox>
                  );
                })}
              </div>
            }
            title={t('views.OpenClassBookingOrderDetails.sessions')}
            trigger="click">
            <Button disabled={!hasSelectedParticipants || !selectedOpenClass}>
              {t('views.OpenClassBookingOrderDetails.sessionPicker', {
                count: selectedSessionIds.length,
              })}
            </Button>
          </Popover>
          <Button disabled={!canAssign} type="primary" onClick={submit}>
            {t('views.OpenClassBookingOrderDetails.assignAttendees', {
              count: selectedCount,
            })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OpenClassBulkAssignmentBar;
